import { Flag } from 'lucide-react';
import type * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  DataTable,
  StatusChip,
  type ChipTone,
  type DataTableState,
  type IDataColumn,
  RowOpens
} from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IOversightResponseRow, ResponseStatus } from '../api/oversight-contracts';
import { responsePath } from '../paths';
import type { ICardLinkState } from './participant-link';
import { ROW_NAME } from './row-name-click';
import { UnderReviewChip } from './under-review-chip';

/**
 * An oversight response row, as a table whose rows open the response — the one rendering of it,
 * shared by the responses list (`GET /api/v1/admin/responses`) and the person hub's Surveys
 * section (SR-215), which draws the rows the hub document carries for the household.
 *
 * One component rather than a second table on the hub, so that a row says the same thing
 * wherever it is read: the same columns, the same status badge, the same open-flag count. The
 * alternative — a compact hub-only list — would be a second rendering of one row type, and
 * the two would drift the first time a column changed.
 *
 * **The participant column is the one thing a caller may drop**, and only the hub does, for a
 * household of one: every row there is the participant the page is about, so the column would
 * repeat the page's title once per row. The rows, their order and their link are unchanged.
 *
 * **Progress is survey-api's count, and a total only where survey-api sends one** (SR-215,
 * person hub §7). `answered` is on every row; `askable` only on a locked response, whose version
 * fixes what it was asked. So a locked response draws a bar and "4 of 10", and any other "4
 * answered" with no total — what a live response can be asked is the rule engine's to say, and
 * a total counted here from the version would be a second evaluator. The branch is on `askable`
 * being sent, not on the status: the server decides when there is a total.
 *
 * **A row opens its response three ways**: a click anywhere on it, Enter on it (the design
 * system's `DataTable` walks the rows with the arrows), and the survey's name as a link, for a new
 * tab. On the list, every one of them leaves the list's search in the history entry, so the
 * response opens beside the list it came from; on the hub, none does, and it opens full width.
 *
 * **Its columns are the list's picker's where it is the list**: version, flags and the last change
 * can be taken away, and the record's dates added — created, first submitted, (last) submitted and
 * the advisor's last edit. **On the hub, which offers no picker, the dates are shown** (`dates`, the
 * advisor record page's F11, Q1, and F12's Q2, ruled (a)) — a record's dates are what the page is
 * read for; the list keeps them behind its picker. The first submission and the advisor's last edit
 * are survey-api's members, drawn as sent; read without them (an API before them), their cells are
 * empty, never "not yet".
 *
 * It renders what it is handed and nothing else — the filters, what a page with no rows says and
 * the pager are the caller's, because the two screens load differently.
 */
export function ResponsesTable({
  rows,
  empty,
  state,
  participantColumn = true,
  search,
  pickerId,
  maxHeight,
  dates = false
}: {
  rows: IOversightResponseRow[];
  /** What an empty page says — which is the caller's to word: "no match" and "none held" are
   *  different claims. */
  empty: React.ReactNode;
  /** What the caller says instead of rows, where it has none yet — a first read out, or failed. */
  state?: DataTableState;
  participantColumn?: boolean;
  /** The list's own query, when the table is the list — its rows then open beside it. */
  search?: string;
  /** Where a viewer's column choice is kept — the list's, not the hub's. */
  pickerId?: string;
  maxHeight?: string;
  /** Show the created and submitted instants by default (a stored picker choice still wins). */
  dates?: boolean;
}) {
  const navigate = useNavigate();
  const state_ =
    state ?? (rows.length === 0 ? { kind: 'empty' as const, content: empty } : undefined);
  const linkState: ICardLinkState | undefined =
    search === undefined ? undefined : { search, list: true };
  const columns = responseColumns(linkState, participantColumn, dates);

  return (
    <DataTable
      columns={columns}
      rows={rows.length === 0 ? null : rows}
      rowKey={(response) => response.responseId}
      onOpen={(response) => navigate(responsePath(response.responseId), { state: linkState })}
      state={state_}
      pickerId={pickerId}
      maxHeight={maxHeight}
    />
  );
}

/** The table's columns — the responses list's work page draws them under its own headers. The ways
 *  in lead the row, as on every work page (SR-530): on the list, beside it (`onPane`) and ↗ the
 *  response in a new tab; on the hub, which has no list, ↗ alone. The column is none the viewer
 *  arranges or hides. */
export function responseColumns(
  linkState: ICardLinkState | undefined,
  participantColumn = true,
  dates = false,
  onPane?: (response: IOversightResponseRow) => void
): IDataColumn<IOversightResponseRow>[] {
  return [
    {
      id: 'open',
      header: <span className="sr-only">Open</span>,
      label: 'Open',
      className: 'w-0 whitespace-nowrap',
      cell: (response) => {
        const name = `${response.participant.displayName} — ${response.survey.code} ${response.cycle.code}`;
        return (
          <RowOpens
            name={name}
            onPane={onPane && (() => onPane(response))}
            to={responsePath(response.responseId)}
            tabWords={`Open ${name} in a new tab`}
          />
        );
      }
    },
    ...(participantColumn
      ? [
          {
            id: 'participant',
            header: 'Participant',
            className: 'font-medium',
            cell: (response: IOversightResponseRow) => response.participant.displayName
          }
        ]
      : []),
    {
      id: 'survey',
      header: 'Survey',
      // The row's click and Enter are the pointer's and the keyboard's; this is a link a reader
      // can open in another tab.
      cell: (response) => (
        <Link
          to={responsePath(response.responseId)}
          state={linkState}
          {...ROW_NAME}
          className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
        >
          {response.survey.name}
        </Link>
      )
    },
    {
      id: 'cycle',
      header: 'Cycle',
      cell: (response) => (
        <>
          {response.cycle.code}
          {response.path && (
            <span className="ml-1 text-xs text-muted-foreground">{response.path.code}</span>
          )}
        </>
      )
    },
    {
      id: 'version',
      header: 'Version',
      optional: true,
      className: 'text-xs text-muted-foreground',
      cell: (response) => `v${response.version.versionNo}`
    },
    {
      id: 'status',
      header: 'Status',
      // The participant's flag, on each of its rows (F9's R11) — beside the status, which every
      // caller draws, rather than the participant column, which the hub drops.
      cell: (response) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <ResponseStatusChip status={response.status} />
          <UnderReviewChip underReview={response.underReview} />
        </span>
      )
    },
    {
      id: 'progress',
      header: 'Progress',
      cell: (response) => <Progress answered={response.answered} askable={response.askable} />
    },
    {
      id: 'flags',
      header: 'Flags',
      optional: true,
      numeric: true,
      cell: (response) =>
        response.openFlags > 0 && (
          <span className="inline-flex items-center gap-1 text-[var(--color-royal-blue)]">
            <Flag className="size-3.5" aria-hidden />
            {response.openFlags}
            <span className="sr-only">open review flags</span>
          </span>
        )
    },
    {
      id: 'updated',
      header: 'Last changed',
      optional: true,
      className: 'text-xs whitespace-nowrap text-muted-foreground',
      cell: (response) => formatDateTime(response.updatedAt)
    },
    {
      id: 'created',
      header: 'Created',
      optional: true,
      hidden: !dates,
      className: 'text-xs whitespace-nowrap text-muted-foreground',
      cell: (response) => formatDateTime(response.createdAt)
    },
    {
      id: 'firstSubmitted',
      header: 'First submitted',
      optional: true,
      hidden: !dates,
      className: 'text-xs whitespace-nowrap text-muted-foreground',
      cell: (response) => sentInstant(response.firstSubmittedAt, 'not yet')
    },
    {
      id: 'submitted',
      header: 'Submitted',
      optional: true,
      hidden: !dates,
      className: 'text-xs whitespace-nowrap text-muted-foreground',
      cell: (response) =>
        response.submittedAt ? formatDateTime(response.submittedAt) : 'not yet'
    },
    {
      id: 'respondentWrite',
      header: 'Advisor’s last edit',
      optional: true,
      hidden: !dates,
      className: 'text-xs whitespace-nowrap text-muted-foreground',
      cell: (response) => sentInstant(response.lastRespondentWriteAt, 'none recorded')
    }
  ];
}

/** An instant survey-api may not send: the instant, `none` for its null, nothing where the member
 *  is absent. */
function sentInstant(value: Nullable<string> | undefined, none: string): string {
  if (value === undefined) return '';
  return value === null ? none : formatDateTime(value);
}

/** A response's status, as the wire spells it — submitted is done, a draft under way, a locked
 *  response settled, and a response taken out of play a plain state. */
export function ResponseStatusChip({ status }: { status: ResponseStatus }) {
  return (
    <StatusChip tone={RESPONSE_STATUS_TONE[status] ?? 'neutral'} dot>
      {status}
    </StatusChip>
  );
}

const RESPONSE_STATUS_TONE: Record<ResponseStatus, ChipTone> = {
  draft: 'info',
  submitted: 'success',
  locked: 'neutral',
  recycled: 'neutral',
  archived: 'neutral'
};

/** A response's progress — see this file's header. A bar only where there is a total. Exported
 *  for a survey tab's strip, which says its one response's progress without a table (SR-407). */
export function Progress({ answered, askable }: { answered: number; askable: Nullable<number> }) {
  if (askable === null) {
    return <span className="text-sm whitespace-nowrap">{answered} answered</span>;
  }
  // The width is clamped; the words are the counts as sent.
  const share = askable > 0 ? Math.min(1, answered / askable) : 0;
  return (
    <span className="grid min-w-24 gap-1">
      <span
        role="progressbar"
        aria-label="Answered"
        aria-valuemin={0}
        aria-valuemax={askable}
        aria-valuenow={Math.min(answered, askable)}
        className="h-1.5 overflow-hidden rounded-full bg-muted"
      >
        <span
          className="block h-full rounded-full bg-[var(--color-sapphire)]"
          style={{ width: `${share * 100}%` }}
        />
      </span>
      <span className="text-sm whitespace-nowrap">
        {answered} of {askable}
      </span>
    </span>
  );
}
